import React, { useState, useEffect } from 'react'
import { t } from 'i18next'
import { FaGlobe, FaPhone, FaSms, FaWhatsapp, FaEnvelope } from 'react-icons/fa'

import * as Sentry from '@sentry/react'
import MyInput from '../../../components/MyInput'
import MyCheckbox from '../../../components/MyCheckbox'
import CookieManager from '../../../lib/CookiesManager'

interface UserContactFormProps {
  formData: {
    phone?: string
    website?: string
    smsOnly?: boolean
    callOnly?: boolean
    whatsapp?: boolean
    emailOnly?: boolean
  }
  handleFieldChange: (key: string, value: any) => void
}

interface CheckboxStates {
  smsOnly: boolean
  callOnly: boolean
  whatsapp: boolean
  emailOnly: boolean
}

interface FormErrors {
  phone?: string
  website?: string
}

export const UserContactForm: React.FC<UserContactFormProps> = ({
  formData,
  handleFieldChange,
}) => {
  const [errors, setErrors] = useState<FormErrors>({})
  const [, setTempPhone] = useState<string>(formData.phone || '')
  const currentPath = window.location.pathname
  const isLibertinAnnounce = currentPath === '/dashboard/announce-libertin/'
  const [website, setWebsite] = useState<string>(formData.website || '')
  const [userStatus, setUserStatus] = useState<string[] | null>(null)
  const [checkboxStates, setCheckboxStates] = useState<CheckboxStates>({
    smsOnly: formData.smsOnly || false,
    callOnly: formData.callOnly || false,
    whatsapp: formData.whatsapp || false,
    emailOnly: formData.emailOnly || false,
  })

  const userId = CookieManager.getUserId() ?? ''
  const token = CookieManager.getToken() ?? ''
  let storageKey = 'myAnnounce'

  if (currentPath === '/dashboard/announce-work/') {
    storageKey = 'myJobOffer'
  } else if (currentPath === '/dashboard/announce-event/') {
    storageKey = 'myEvent'
  } else if (currentPath === '/dashboard/announce-establishment/') {
    storageKey = 'myEstablishment'
  }

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem(storageKey) || '{}')
    setCheckboxStates({
      smsOnly: storedData.smsOnly !== undefined ? storedData.smsOnly : false,
      callOnly: storedData.callOnly !== undefined ? storedData.callOnly : false,
      whatsapp: storedData.whatsapp !== undefined ? storedData.whatsapp : false,
      emailOnly:
        storedData.emailOnly !== undefined ? storedData.emailOnly : false,
    })
    setWebsite(storedData.website || '')
    setTempPhone(storedData.phone || '')

    const updatedData = {
      ...storedData,
      smsOnly: storedData.smsOnly !== undefined ? storedData.smsOnly : false,
      callOnly: storedData.callOnly !== undefined ? storedData.callOnly : false,
      whatsapp: storedData.whatsapp !== undefined ? storedData.whatsapp : false,
      emailOnly:
        storedData.emailOnly !== undefined ? storedData.emailOnly : false,
    }
    localStorage.setItem(storageKey, JSON.stringify(updatedData))
  }, [])

  const validateWebsite = (website: string): string | undefined => {
    const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\-]*)*\/?$/

    if (!website) return undefined

    if (website.length > 200) {
      return t('validation.too-long')
    }

    if (!urlPattern.test(website)) {
      return t('invalid-url')
    }

    return undefined
  }

  const handleWebsiteChange = (value: string) => {
    setWebsite(value)
    const websiteError = validateWebsite(value)
    setErrors(prev => ({ ...prev, website: websiteError }))

    if (!websiteError || !value) {
      handleFieldChange('website', value)
      updateLocalStorage('website', value)
    }
  }

  const handleCheckboxChange = (key: keyof CheckboxStates, value: boolean) => {
    let newCheckboxStates = { ...checkboxStates, [key]: value }

    if (key === 'smsOnly' && value) {
      newCheckboxStates.callOnly = false
    } else if (key === 'callOnly' && value) {
      newCheckboxStates.smsOnly = false
    }

    setCheckboxStates(newCheckboxStates)

    Object.keys(newCheckboxStates).forEach(k => {
      handleFieldChange(k, newCheckboxStates[k as keyof CheckboxStates])
      updateLocalStorage(k, newCheckboxStates[k as keyof CheckboxStates])
    })
  }

  const updateLocalStorage = (key: string, value: any) => {
    try {
      const storedData = JSON.parse(localStorage.getItem(storageKey) || '{}')
      storedData[key] = value
      localStorage.setItem(storageKey, JSON.stringify(storedData))
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          action: 'updateLocalStorage',
          key,
          value,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'local_storage_error',
          method: 'updateLocalStorage',
        },
      })
    }
  }

  return (
    <div className='flex flex-col min-h-0 max-h-[calc(100vh-300px)] overflow-y-auto'>
      {!isLibertinAnnounce && (
        <div className='w-full mb-2'>
          <MyInput
            label={t('create-ad.website')}
            value={website}
            onChange={handleWebsiteChange}
            icon={<FaGlobe />}
            error={errors.website}
            isInvalid={!!errors.website}
          />
        </div>
      )}
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2'>
        <div className='w-full bg-grisclair text-sm rounded-lg cursor-pointer'>
          <MyCheckbox
            name='whatsapp'
            label={t('create-ad.whatsapp-available')}
            checked={checkboxStates.whatsapp}
            onChange={checked => handleCheckboxChange('whatsapp', checked)}
            icon={<FaWhatsapp />}
          />
        </div>
        <div className='w-full bg-grisclair text-sm rounded-lg cursor-pointer'>
          <MyCheckbox
            name='emailOnly'
            label={t('create-ad.email-available')}
            checked={checkboxStates.emailOnly}
            onChange={checked => handleCheckboxChange('emailOnly', checked)}
            icon={<FaEnvelope />}
          />
        </div>
        <div className='w-full bg-grisclair text-sm rounded-lg cursor-pointer'>
          <MyCheckbox
            name='smsOnly'
            label={t('create-ad.sms-only')}
            checked={checkboxStates.smsOnly}
            onChange={checked => handleCheckboxChange('smsOnly', checked)}
            icon={<FaSms />}
          />
        </div>
        <div className='w-full bg-grisclair text-sm rounded-lg cursor-pointer'>
          <MyCheckbox
            name='callOnly'
            label={t('create-ad.call-only')}
            checked={checkboxStates.callOnly}
            onChange={checked => handleCheckboxChange('callOnly', checked)}
            icon={<FaPhone />}
          />
        </div>
      </div>
    </div>
  )
}

export default UserContactForm
